import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Article as ArticleIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import {
  useCaseAttachmentContext,
  type Attachment,
} from '../../contexts/api-contexts/CaseAttachmentContext';

interface AttachmentViewDialogProps {
  open: boolean;
  onClose: () => void;
  attachmentId: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`attachment-tabpanel-${index}`}
      aria-labelledby={`attachment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `attachment-tab-${index}`,
    'aria-controls': `attachment-tabpanel-${index}`,
  };
}

export const AttachmentViewDialog: React.FC<AttachmentViewDialogProps> = ({
  open,
  onClose,
  attachmentId,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const { ensureAttachment } = useCaseAttachmentContext();
  const theme = useTheme();
  const isWideScreen = useMediaQuery(theme.breakpoints.up('xl')); // xl = 1536px

  // Load attachment data when dialog opens or attachment ID changes
  useEffect(() => {
    if (open && attachmentId) {
      setLoading(true);
      setPreviewError(false);
      ensureAttachment(attachmentId)
        .then((attachmentData) => {
          setAttachment(attachmentData || null);
        })
        .catch((error) => {
          console.error('Error loading attachment:', error);
          setAttachment(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, attachmentId, ensureAttachment]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPreviewError(false);
  };

  const handleDownload = () => {
    if (attachment?.download_url) {
      window.open(attachment.download_url, '_blank');
    }
  };

  const isImageMimeType = (mimeType: string): boolean => {
    return mimeType.startsWith('image/');
  };

  const renderPreviewContent = () => {
    if (!attachment) return null;

    if (isImageMimeType(attachment.mime_type)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <img
            src={attachment.download_url || ''}
            alt={attachment.filename}
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
            }}
            onError={() => setPreviewError(true)}
          />
        </Box>
      );
    } else {
      // For non-image files, try to show in iframe
      return (
        <Box sx={{ height: '70vh', position: 'relative' }}>
          {!previewError ? (
            <iframe
              src={attachment.download_url || ''}
              title={attachment.filename}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: theme.shape.borderRadius,
              }}
              onError={() => setPreviewError(true)}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 2,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Preview not available for this file type
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={!attachment.download_url}
              >
                Download File
              </Button>
            </Box>
          )}
        </Box>
      );
    }
  };

  const renderMarkdownContent = () => {
    if (!attachment) return null;

    if (attachment.extracted_text) {
      return (
        <TextField
          fullWidth
          multiline
          minRows={20}
          value={attachment.extracted_text}
          variant="outlined"
          InputProps={{ readOnly: true }}
          sx={{ '& .MuiInputBase-input': { fontSize: '0.9rem' } }}
        />
      );
    } else {
      return (
        <Alert severity="info">
          No text content could be extracted from this file.
        </Alert>
      );
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: isWideScreen ? '90vw' : '80vw',
          height: '85vh',
          maxWidth: '1400px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {attachment?.filename || 'Loading...'}
        </Typography>
        {attachment?.download_url && (
          <IconButton
            aria-label="download"
            onClick={handleDownload}
            sx={{ mr: 1 }}
          >
            <DownloadIcon />
          </IconButton>
        )}
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: isWideScreen ? 'row' : 'column',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : attachment ? (
          <>
            {/* Tabs - horizontal on wide screens, vertical otherwise */}
            <Box
              sx={{
                borderRight: isWideScreen ? 1 : 0,
                borderBottom: isWideScreen ? 0 : 1,
                borderColor: 'divider',
                minWidth: isWideScreen ? '200px' : 'auto',
              }}
            >
              <Tabs
                orientation={isWideScreen ? 'vertical' : 'horizontal'}
                variant="fullWidth"
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  ...(isWideScreen && {
                    '& .MuiTabs-indicator': {
                      left: 0,
                      right: 'auto',
                    },
                  }),
                }}
              >
                <Tab
                  icon={<ArticleIcon />}
                  label="Extracted Text"
                  {...a11yProps(0)}
                  sx={{ textTransform: 'none' }}
                />
                <Tab
                  icon={<PreviewIcon />}
                  label="File Preview"
                  {...a11yProps(1)}
                  sx={{ textTransform: 'none' }}
                />
              </Tabs>
            </Box>

            {/* Tab content */}
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <TabPanel value={tabValue} index={0}>
                <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
                  {renderMarkdownContent()}
                </Box>
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ height: '100%', overflow: 'auto' }}>
                  {renderPreviewContent()}
                </Box>
              </TabPanel>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <Alert severity="error">Failed to load attachment data</Alert>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AttachmentViewDialog;
